import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getFoldersQueryOptions, getTagsQueryOptions, mineQueryKeys } from "../query-options";
import type { FolderData, TagData } from "../types";

const EMPTY_FOLDERS: FolderData[] = [];
const EMPTY_TAGS: TagData[] = [];

/**
 * Navigation 子域的数据 hooks：
 * - 文件夹、标签查询
 * - Sidebar 数据聚合
 * - 导航区域的刷新动作
 */
export function useFoldersQuery() {
  return useQuery(getFoldersQueryOptions());
}

export function useTagsQuery() {
  return useQuery(getTagsQueryOptions());
}

export function useFolders(): FolderData[] {
  const query = useFoldersQuery();
  return query.data?.folders ?? EMPTY_FOLDERS;
}

export function useTags(): TagData[] {
  const query = useTagsQuery();
  return query.data?.tags ?? EMPTY_TAGS;
}

export function useSidebarActions() {
  const queryClient = useQueryClient();

  return {
    refreshFolders: async () => {
      await queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders });
    },
    refreshTags: async () => {
      await queryClient.invalidateQueries({ queryKey: mineQueryKeys.tags });
    },
    refreshSidebarData: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders }),
        queryClient.invalidateQueries({ queryKey: mineQueryKeys.tags }),
      ]);
    },
  };
}

export function useSidebarData() {
  const foldersQuery = useFoldersQuery();
  const tagsQuery = useTagsQuery();

  return {
    folders: foldersQuery.data?.folders ?? EMPTY_FOLDERS,
    tags: tagsQuery.data?.tags ?? EMPTY_TAGS,
    isLoading: foldersQuery.isPending || tagsQuery.isPending,
    isFetching: foldersQuery.isFetching || tagsQuery.isFetching,
    error: foldersQuery.error ?? tagsQuery.error ?? null,
  };
}
